import React, { createContext, useReducer } from 'react'
import { SET_AUTHENTICATION, SET_USER_DETAILS } from './actionTypes'

type Nullable<T> = T | null

type Action = {
  type: string,
  payload: Record<string, unknown>
}

type ContextState = {
  state: {
    auth: {
      accessToken: Nullable<string>,
      isAuthenticated: boolean,
      hasCheckedAuthentication: boolean,
    },
    user: {
      id: Nullable<string>,
      firstName: Nullable<string>,
      lastName: Nullable<string>,
      email: Nullable<string>,
      goustoReference: Nullable<string>,
    },
  },
  dispatch: React.Dispatch<Action>
}

export const initialState: ContextState['state'] = {
  auth: {
    accessToken: null,
    isAuthenticated: false,
    hasCheckedAuthentication: false,
  },
  user: {
    id: null,
    firstName: null,
    lastName: null,
    email: null,
    goustoReference: null,
  },
}

export const store = createContext<ContextState>({ state: initialState, dispatch: () => {} })

export interface StateProviderProps {
  overrideDispatch?: React.Dispatch<Action>
}

export const StateProvider: React.FC<StateProviderProps> = ({ children, overrideDispatch }) => {
  const [state, originalDispatch] = useReducer((currentState: ContextState['state'], action: Action) => {
    switch (action.type) {
      case SET_AUTHENTICATION: {
        const newState = {
          ...currentState,
          auth: {
            ...currentState.auth,
            ...action.payload,
          },
        }

        return newState
      }
      case SET_USER_DETAILS: {
        const newState = {
          ...currentState,
          user: {
            ...currentState.user,
            ...action.payload,
          },
        }

        return newState
      }
      default:
        throw new Error('Invalid action type')
    }
  }, initialState)

  const dispatch = overrideDispatch || originalDispatch

  const value = React.useMemo(() => ({ state, dispatch }), [state, dispatch])

  return <store.Provider value={value}>{children}</store.Provider>
}
